/**
 * Telegram Mini App bridge. Everything here is synchronous and local: the SDK
 * talks to the Telegram client through postEvent, never to the network.
 * Outside Telegram (plain browser) every call degrades to a no-op.
 */
import {
  backButton,
  downloadFile,
  hapticFeedbackImpactOccurred,
  hapticFeedbackNotificationOccurred,
  init,
  isTMA,
  miniApp,
  openLink,
  openTelegramLink,
  retrieveLaunchParams,
  retrieveRawInitData,
  shareStory,
  swipeBehavior,
  viewport,
} from '@telegram-apps/sdk-react';

export interface TgEnv {
  inTelegram: boolean;
  initDataRaw: string | null;
  platform: string;
  startParam: string | null;
}

let env: TgEnv = { inTelegram: false, initDataRaw: null, platform: 'web', startParam: null };

export function bootTelegram(): TgEnv {
  // 1. Launch params are read from the URL/sessionStorage: local, synchronous, no bridge needed.
  try {
    if (!isTMA()) return env;
    const lp = retrieveLaunchParams();
    env = {
      inTelegram: true,
      initDataRaw: retrieveRawInitData() ?? null,
      platform: lp.tgWebAppPlatform,
      startParam: lp.tgWebAppStartParam ?? null,
    };
  } catch (e) {
    /* not inside Telegram: the app still renders */
    console.debug('[tg] not a Mini App environment:', e);
    return env;
  }
  // 2. The bridge (postEvent) may be unavailable in exotic clients; auth does not depend on it.
  try {
    init();
    if (miniApp.mount.isAvailable()) {
      miniApp.mount();
      if (miniApp.setHeaderColor.isAvailable()) miniApp.setHeaderColor('#fdf4e3');
      if (miniApp.setBackgroundColor.isAvailable()) miniApp.setBackgroundColor('#fdf4e3');
      if (miniApp.setBottomBarColor.isAvailable()) miniApp.setBottomBarColor('#fdf4e3');
    }
    if (backButton.mount.isAvailable()) backButton.mount();
    if (viewport.mount.isAvailable()) {
      void viewport
        .mount()
        .then(() => {
          if (viewport.bindCssVars.isAvailable()) viewport.bindCssVars();
          if (viewport.expand.isAvailable()) viewport.expand();
        })
        .catch(() => {});
    }
    if (swipeBehavior.mount.isAvailable()) {
      swipeBehavior.mount();
      if (swipeBehavior.disableVertical.isAvailable()) swipeBehavior.disableVertical();
    }
    if (miniApp.ready.isAvailable()) miniApp.ready();
  } catch (e) {
    console.debug('[tg] bridge init failed, continuing without it:', e);
  }
  return env;
}

export function getTgEnv(): TgEnv {
  return env;
}

export function showBackButton(show: boolean) {
  try {
    if (!env.inTelegram) return;
    if (show && backButton.show.isAvailable()) backButton.show();
    if (!show && backButton.hide.isAvailable()) backButton.hide();
  } catch {
    /* ignore */
  }
}

export function onBackButton(fn: () => void): () => void {
  try {
    if (env.inTelegram && backButton.onClick.isAvailable()) return backButton.onClick(fn);
  } catch {
    /* ignore */
  }
  return () => {};
}

export function haptic(style: 'light' | 'medium' | 'soft' = 'light') {
  try {
    if (hapticFeedbackImpactOccurred.isAvailable()) hapticFeedbackImpactOccurred(style);
  } catch {
    /* ignore */
  }
}

export function openExternal(url: string) {
  try {
    if (env.inTelegram && /^https:\/\/t\.me\//.test(url) && openTelegramLink.isAvailable()) return openTelegramLink(url);
    if (env.inTelegram && openLink.isAvailable()) return openLink(url, { tryInstantView: false });
  } catch {
    /* fall through */
  }
  window.open(url, '_blank', 'noopener');
}

export function hapticSuccess() {
  try {
    if (hapticFeedbackNotificationOccurred.isAvailable()) hapticFeedbackNotificationOccurred('success');
  } catch {
    /* ignore */
  }
}

/** Telegram Stories exist only in recent mobile clients; the button is hidden where they do not. */
export function canShareStory(): boolean {
  try {
    return env.inTelegram && shareStory.isAvailable();
  } catch {
    return false;
  }
}

export function shareToStory(mediaUrl: string, text: string, link: { url: string; name: string } | null) {
  try {
    if (shareStory.isAvailable()) shareStory(mediaUrl, { text, ...(link ? { widgetLink: link } : {}) });
  } catch {
    /* the client refused — nothing to do */
  }
}

/** Saves a file through Telegram (Bot API 8.0); in a browser, a normal download. */
export async function saveFile(url: string, fileName: string): Promise<void> {
  try {
    if (env.inTelegram && downloadFile.isAvailable()) {
      await downloadFile(url, fileName);
      return;
    }
  } catch {
    /* fall back to the browser */
  }
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
